<script setup lang="ts">
import type { NewsCategoryFragment, NewsThumbFragment } from '#graphql-operations'

interface RelatedNewsProps {
  id: string | number
  categories: NewsCategoryFragment[]
  relateds: NewsThumbFragment[]
}

const props = defineProps<RelatedNewsProps>()
const { locale } = useI18n()
const carouselId = computed(() => `related-${props.id}`)

const { data: relateds } = await useAsyncData(`relatedNews-${props.id}`, async () => {
  if (props.relateds.length) {
    return props.relateds
  }

  const res = await useGraphqlQuery('Entries', {
    limit: 3,
    section: ['news'],
    id: ['not', props.id],
    site: [locale.value],
    relatedTo: props.categories.map(c => c.id),
  })

  return (res.data.entries?.filter?.(Boolean) || []) as NewsThumbFragment[]
})
</script>

<template>
  <Section v-if="(relateds?.length || 0) > 0" padded class="relatedNews space-y-6 lg:space-y-12">
    <div class="container layout-grid">
      <div class="col-span-full">
        <Text class="typo-sans-1 text-center uppercase" as="h2" :text="$t('related_articles')" />
      </div>
    </div>
    <div class="desktop-only">
      <ul class="container layout-grid">
        <li v-for="news in relateds" :key="news.id" class="col-span-4">
          <CardNews :data="news" />
        </li>
      </ul>
    </div>
    <div class="mobile-only">
      <BlossomCarousel :id="carouselId" as="ul" class="carousel size-full">
        <li
          v-for="news in relateds"
          :key="news.id"
          data-blossom-slide
          class="slide flex-shrink-0"
        >
          <CardNews :data="news" />
        </li>
      </BlossomCarousel>
      <div class="container c-$accent-color mt-5 flex gap-4 items-center justify-between lg:mt-12.5">
        <BlossomPrev :for="carouselId" :aria-label="$t('previous')">
          <ButtonIcon as="span" icon="chevron-left" />
        </BlossomPrev>
        <BlossomDots :for="carouselId" class="flex gap-2 justify-center" />
        <BlossomNext :for="carouselId" :aria-label="$t('next')">
          <ButtonIcon as="span" icon="chevron-right" />
        </BlossomNext>
      </div>
    </div>
    <!-- <ul class="container c-$accent-color mt-5 flex gap-2 w-full items-center justify-center lg:c-wine lg:mt-12.5">
      <li v-for="link in data.links" :key="link!.id">
        <ButtonLink :link="link?.customLink" />
      </li>
    </ul> -->
  </Section>
</template>

<style scoped>
.relatedNews .carousel {
  overflow: auto hidden;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;

  @screen lt-lg {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 100%;
    column-gap: calc(var(--spacing) * 4);
    padding-inline: calc(var(--spacing) * 4);
    scroll-padding-inline: calc(var(--spacing) * 4);
  }
}

.relatedNews :deep([data-blossom-slide]) {
  scroll-snap-align: start;
}

.relatedNews :deep(.blossom-dot) {
  background-color: var(--blossom-dot-color, currentColor);
  border-radius: 100%;

  inline-size: calc(var(--spacing) * 1.8);
  block-size: calc(var(--spacing) * 2);

  opacity: var(--blossom-dot-opacity, 0.35);

  &[aria-current='true'] {
    opacity: 1;
  }
}
</style>
